import { useEffect, useState } from 'react';

export type UseDelayedStateOptions<T> = {
  /** How long to wait before committing the state change */
  delay: number;
  /** The initial state value */
  initialState?: T;
  /** Should the state be delayed? */
  enabled?: boolean;
};

/**
 * Defer the update of a value by a specified delay.
 *
 * @param state The next state value
 * @param delayedStateOptions The options for delaying the state change
 * @example
 *
 * ```js
 * const [show, setShow] = useState(false)
 * const delayedShow = useDelay(show, { delay: 500 })
 * ```
 *
 * if `show` changes from false to true, after 500ms delayedShow
 * will change to true. This allows you to declaratively respond
 * to state changes after a specified duration.
 *
 */
export function useDelayedState<T>(
  state: T,
  { delay, initialState, enabled = true }: UseDelayedStateOptions<T>
) {
  const [delayed, setDelayed] = useState(initialState);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (enabled) {
        setDelayed(state);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [state, delay, enabled]);

  return enabled ? delayed : state;
}
